define('home/views/DiscussionCreateView',[
    'underscore',
    'backbone-marionette',

    'core/mixins/withEmailVerifyLink',
    'core/strings',

    'home/mixins/withSubviews',
    'home/models/Thread',
    'home/templates/discussionCreate',

    'home/utils/features',
    'home/utils/backboneUtils',
    'home/utils/confirmModal',
    'home/utils/trackingUtils',
    'home/views/PostReplyView',
    'home/views/EditDiscussionCategoryView',
    'home/views/TopicInputView',
    'home/views/ChannelBannerView',
    'home/views/DiscussionCreateToolbarView',
    'home/templates/discussionChannelInfo',
    'home/templates/threadSubmitError',
], function (
    _,
    Marionette,

    withEmailVerifyLink,
    strings,

    withSubviews,
    Thread,
    discussionCreateTemplate,

    featureUtils,
    backboneUtils,
    confirmModal,
    trackingUtils,
    PostReplyView,
    EditDiscussionCategoryView,
    TopicInputView,
    ChannelBannerView,
    DiscussionCreateToolbarView,
    discussionChannelInfoTmpl,
    threadSubmitErrorTemplate
) {
    'use strict';

    var gettext = strings.get;

    var parent = Marionette.ItemView;
    var parentProto = parent.prototype;


    var DiscussionCreateView = parent.extend({
        template: discussionCreateTemplate,
        templateHelpers: function () {
            var modEmail = this.model.thread.forum.channel.get('options').modEmail;
            var isChannelModerator = _.contains(this.session.moderatedForums, this.model.thread.forum.id);

            return {
                isNew: this.isNew,
                sessionUserNumPosts: this.session.user.get('numPosts'),
                sessionUserVerified: this.session.user.get('isVerified'),
                emailHref: modEmail ? 'mailto:' + modEmail : '',
                channelName: this.model.thread.forum.channel.get('name'),
                channelUrl: '/home/channel/' + this.model.thread.forum.channel.get('slug') + '/',
                canInviteActiveUsers: isChannelModerator,
                canModifyTopics: this.canModifyTopics,
            };
        },

        events: {
            'submit': 'submitThread',
            'click [data-action=cancel-create]': 'navigateBack',
            'blur input[name=title]': 'updateTitle',
        },

        ui: {
            title: '[name=title]',
            subscribe: '[name=subscribe]',
            inviteActiveUsers: '[name=invite-active-users]',
        },

        initialize: function (options) {
            var thread = this.model.thread;
            this.isNew = thread.isNew();
            trackingUtils.setSection(this.isNew ? 'create' : 'edit');

            this.listenTo(this.model, 'change:category', this.categoryChanged);
            this.listenTo(this.model, 'change', this.updateValidationErrors);
            this.listenTo(this.model.topics, 'add remove reset', this.updateValidationErrors);
            this.listenTo(this.model, 'invalid', this.handleValidationError);

            this.session = options.session;
        },

        regions: {
            postboxRegion: '[data-role=postbox]',
            channelInfoRegion: '[data-role=channel-info]',
            categoriesRegion: '[data-role=channel-categories]',
            topicInputRegion: '[data-role=topic-input]',
            bannerRegion: '[data-role=banner]',
            toolbarRegion: '[data-role=toolbar]',
        },

        getThread: function () {
            return this.model.thread;
        },

        getForum: function () {
            return this.model.thread.forum;
        },

        getMetaAttrs: function () {
            return backboneUtils.getPromiseFor(this.model.thread.forum, 'name')
                .then(function (name) {
                    return strings.interpolate(gettext('Create new discussion on %(name)s'), { name: name });
                });

        },

        render: function () {
            parentProto.render.call(this);

            if (!this.session.fetched)
                return void this.session.fetch().then(_.bind(this.render, this));

            this.activateRegion(this.postboxRegion, this.createPostbox);
            this.activateRegion(this.toolbarRegion, this.createToolbarView);

            var channel = this.model.thread.forum.channel;
            if (channel) {
                this.activateRegion(this.channelInfoRegion, this.createChannelView);
                this.activateRegion(this.bannerRegion, this.createBanner);
                this.activateRegion(this.topicInputRegion, this.createTopicInput);

                var categories = channel.getCategories();
                if (categories)
                    this.activateRegion(this.categoriesRegion, this.createCategoriesView);
            }

            return this;
        },

        createToolbarView: function () {
            return new DiscussionCreateToolbarView({
                el: this.toolbarRegion.el,
                model: this.model,
                getPostbox: _.bind(function () {
                    return this.postboxRegion.currentView;
                }, this),
            });
        },

        createBanner: function () {
            return new ChannelBannerView({
                el: this.bannerRegion.el,
                model: this.model.thread.forum.channel,
            });
        },

        createChannelView: function () {
            return new Marionette.ItemView({
                el: this.channelInfoRegion.el,
                model: this.model.thread.forum.channel,
                template: discussionChannelInfoTmpl,
            });
        },

        createCategoriesView: function () {
            var view = new EditDiscussionCategoryView({
                model: this.model,
            });

            this.bindTitleCategoryHandlers();

            return view;
        },

        /**
         * Adds the chosen category as a prefix to the title input and
         * makes that part of the input read-only.
         */
        categoryChanged: function (_model, newCategory) {
            var $title = this.ui.title;

            // Remove any existing category prefix from the title
            var prevCategory = this.model._previousAttributes.category;
            if (prevCategory) {
                var strippedTitle = $title.val().match(
                    new RegExp(prevCategory + Thread.CATEGORY_TITLE_SEPARATOR + '(.*)')
                );

                if (strippedTitle)
                    $title.val(strippedTitle[1]);
            }

            $title.val(newCategory + Thread.CATEGORY_TITLE_SEPARATOR + $title.val());
            this.updateTitle();
        },

        /**
         * Adds listeners for input into the title input which prevent the user from
         * editing or deleting the category.
         */
        bindTitleCategoryHandlers: function () {
            if (this.titleCategoryHandlerBound)
                return;

            var $title = this.ui.title;

            // Use a partial here to pass this view's model as an argument to the handler so
            // that we don't overwrite the context, which is used, and so we don't
            // reference variables outside the function scope, which is more
            // expensive, on a handler that is called many many times.
            var titleCategoryHandler = _.partial(function (model, evt) {
                var category = model.get('category');
                var prefixLength = category && (category + Thread.CATEGORY_TITLE_SEPARATOR).length;

                // Prevent keypresses other than the allowed keys while in the
                // read-only category section of the title input, and prevent
                // backspace if at the end of the category section.
                if (!_.contains(DiscussionCreateView.NON_EDITING_KEYS, evt.which) &&
                    ((this.selectionStart < prefixLength) ||
                    (this.selectionStart === prefixLength && evt.which === 8))
                )
                    return false;
            }, this.model);

            $title.on('keydown', titleCategoryHandler);

            this.titleCategoryHandlerBound = true;
        },

        submitThread: function (ev) {
            ev.preventDefault();

            var postbox,
                message;

            if (this._submitLocked || !this.model.isValid())
                return;

            // Save the text in the form to thread.message
            postbox = this.postboxRegion.currentView;
            message = postbox.textarea.get();

            if (!this._oldMessage && !this._oldRawMessage) {
                // Should not override to preserve the original message
                // in case the user unsuccessfully submits multiple times
                // then cancels
                this._oldMessage = this.model.thread.get('message');
                this._oldRawMessage = this.model.thread.get('raw_message');
            }

            this._submitLocked = this.model.thread.save({
                title: this.ui.title.val(),
                message: message,
                shouldSanitizeMessage: true,
            }, {
                success: _.bind(this.handleThreadCreate, this),
                error: _.bind(this.handleSaveError, this),
            });

            if (this._submitLocked) {
                this.$el.find('form').addClass('is-submitting');
                this.$el.find('#submit-button').prop('disabled', true);
                this.$el.find('#cancel-button').prop('disabled', true);
            }
        },

        createPostbox: function () {
            var postbox = new PostReplyView({
                // Normally you pass a post model to PostReplyView but it turns out
                // the thread model and post model are both similar enough to be
                // consumed by PostReplyView
                model: this.model.thread,
                // Thread must also be passed here; while the model was used
                // for basic functionality, thread is used to check settings
                thread: this.model.thread,
                placeholder: gettext('Tell people what you want your discussion to be about.  Use gifs and images too.'),
                session: this.session,
                canUseContentEditable: featureUtils.canUseContentEditable(),
                showSubmitButton: false,
                showAvatar: false,
                showMediaPreviews: true,
                showMustVerifyAlert: false,
            });

            this.listenTo(postbox, 'blur', this.updateMessage);

            return postbox;
        },

        createTopicInput: function () {
            return new TopicInputView({
                collection: this.model.topics,
            });
        },

        handleThreadCreate: function () {
            this.model.thread.set('shouldSanitizeMessage', false); // successfully created, have sanitized response
            if (typeof this.ui.subscribe !== 'object' || typeof this.ui.inviteActiveUsers !== 'object')
                // User navigated away from page before thread was created,
                // can no longer access check boxes
                return;

            // Should the author be subscribed to this thread upon thread creation
            var shouldSubscribe = this.ui.subscribe.prop('checked');
            if (shouldSubscribe)
                this.model.thread.subscribe();

            // Whether the most active users in this channel should be invited
            var shouldInviteActiveUsers = this.ui.inviteActiveUsers.prop('checked');
            if (shouldInviteActiveUsers)
                this.model.thread.inviteUserGroup('good_forum_commenters');

            // Save any topics that might have been added or removed
            this.model.saveVisibleTopics();

            if (this.isNew)
                // Indicate that this thread has been recently created
                this.model.thread.set('isNew', true);

            // Trigger 'createThread' so code upstream knows we're
            // done creating/updating this original thread
            this.trigger('createThread');
        },

        updateTitle: function () {
            this.model.set({
                title: this.ui.title.val(),
            });
        },

        updateMessage: function () {
            var postbox = this.postboxRegion.currentView;

            this.model.set({
                message: postbox.textarea.get(),
            });
        },

        updateValidationErrors: function () {
            // Assume all inputs are valid, remove the invalid styling
            this.$('.form-attribute-input').removeClass('is-error');

            // Run validation, if it fails, it will trigger an invalid event
            // which will cause the invalid styling to reappear
            this.model.isValid();
        },

        handleValidationError: function (_model, errs) {
            _.each(errs, function (err) {
                // Attribute input elements may have multiple attributes specified in
                // data-attribute, because there may be multiple attributes editable
                // in that input group and errors for all of those attributes should
                // show up in the same way.
                this.$('.form-attribute-input[data-attribute*=' + err.attrName + ']')
                    .addClass('is-error')
                    .find('[data-role=attribute-error]')
                    .text(err.message);
            }, this);
        },

        handleSaveError: function (_thread, response) {
            this._submitLocked = false;
            this.$('form').removeClass('is-submitting');
            this.$('#cancel-button').prop('disabled', false);
            this.$('#submit-button').prop('disabled', false);
            confirmModal.confirmationPromise(
                confirmModal.TYPES.CLOSE,
                threadSubmitErrorTemplate(response)
            );
        },

        navigateBack: function () {
            if (this.model.thread.id) {
                // Thread exists, so user was editing it
                if (this._oldMessage && this._oldRawMessage) {
                    this.model.thread.set({
                        message: this._oldMessage,
                        raw_message: this._oldRawMessage,
                        shouldSanitizeMessage: false, // resetting messages to originals from server
                    });
                }
                this.trigger('createThread');
            } else {
                // User canceled creating new thread
                this.trigger('cancelThread');
            }
        },

        getPageTrackingParams: function () {
            return {
                forum: this.model.thread.forum.id,
            };
        },
    }, {
        // Which keys we allow to have effect while in
        // the category prefix part of the title input
        NON_EDITING_KEYS: [
            37, // left arrow
            39, // right arrow
            36, // home
            35, // end
            9, // tab
            13, // enter
        ],
    });


    withSubviews.call(DiscussionCreateView.prototype);
    withEmailVerifyLink.call(DiscussionCreateView.prototype);

    return DiscussionCreateView;
});

